import React, { useState, useEffect } from 'react';
import { Users, Music, Calendar, MapPin, Phone, Mail, Globe, CheckCheck, Camera } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ProfileHeader from './components/ProfileHeader';
import AgentSection from './components/AgentSection';
import InfluencerMetrics from './components/InfluencerMetrics';
import MediaGallery from './components/MediaGallery';
import PerformanceHistory from './components/PerformanceHistory';
import BookingAvailability from './components/BookingAvailability';

const EnhancedArtistProfile = () => {
  const [artistData, setArtistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Renommé pour éviter unused var (pas encore utilisé dans l'UI)
  const [_currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    getCurrentUser();
    fetchArtistData();
  }, []);

  const getCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.from('user_profiles').select('*').eq('id', user?.id).single();
        setCurrentUser(profile);
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchArtistData = async () => {
    try {
      setError(null);
      
      // First try to get an artist (any partner that could represent an artist)
      // Priority: partners with artist profession, then any active partner
      let artist = null;
      let artistError = null;

      // Try to get a partner with artist profession first
      const { data: artistWithProfession, error: professionError } = await supabase
        .from('partners')
        .select(`
          *,
          user_profiles (
            id,
            full_name,
            email,
            avatar_url,
            created_at
          ),
          partner_musical_specialties (
            specialty
          ),
          partner_profession_data (
            profession,
            skill_tags,
            performance_styles,
            equipment_list,
            specialization_data
          ),
          partner_social_links (
            platform,
            username,
            profile_url,
            follower_count,
            is_verified,
            is_primary
          ),
          partner_portfolio_media (
            id,
            title,
            media_type,
            file_url,
            thumbnail_url,
            category,
            is_featured
          )
        `)
  .not('artist_profession', 'is', null)
  .eq('status', 'active')
  .limit(1)
  .maybeSingle();

      if (!professionError && artistWithProfession) {
        artist = artistWithProfession;
      } else {
        // Fallback: get any active partner
        const { data: anyPartner, error: anyPartnerError } = await supabase
          .from('partners')
          .select(`
            *,
            user_profiles (
              id,
              full_name,
              email,
              avatar_url,
              created_at
            ),
            partner_musical_specialties (
              specialty
            ),
            partner_profession_data (
              profession,
              skill_tags,
              performance_styles,
              equipment_list,
              specialization_data
            ),
            partner_social_links (
              platform,
              username,
              profile_url,
              follower_count,
              is_verified,
              is_primary
            ),
            partner_portfolio_media (
              id,
              title,
              media_type,
              file_url,
              thumbnail_url,
              category,
              is_featured
            )
          `)
          .eq('status', 'active')
          .limit(1)
          .maybeSingle();

        if (!anyPartnerError && anyPartner) {
          artist = anyPartner;
        } else {
          artistError = anyPartnerError || new Error('No artists found');
        }
      }

      if (!artist) {
        throw artistError || new Error('No artist data available');
      }

      // Get agent assignments (only if artist exists)
      const { data: agents, error: agentsError } = await supabase
        .from('agent_assignments')
        .select(`
          *,
          agent:partners!agent_id (
            id,
            business_name,
            user_id,
            phone,
            email,
            average_rating,
            total_bookings,
            user_profiles (
              full_name,
              avatar_url
            )
          )
        `)
        .eq('artist_id', artist?.id);

      if (agentsError) console.error('Error fetching agents:', agentsError);

      // Get influencer followers
      const { data: influencers, error: influencersError } = await supabase
        .from('influencer_follows')
        .select(`
          *,
          influencer:partners!influencer_id (
            id,
            business_name,
            user_id,
            user_profiles (
              full_name,
              avatar_url
            )
          )
        `)
        .eq('artist_id', artist?.id);

      if (influencersError) console.error('Error fetching influencers:', influencersError);

      // Get recent bookings (performance history)
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          *,
          campaign:campaigns (
            title,
            description,
            start_date,
            end_date
          )
        `)
        .eq('partner_id', artist?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (bookingsError) console.error('Error fetching bookings:', bookingsError);

      setArtistData({
        artist,
        agents: agents || [],
        influencers: influencers || [],
        bookings: bookings || []
      });
    } catch (error) {
      console.error('Error fetching artist data:', error);
      setError(error?.message || 'Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleContactAgent = (_agentId) => {
    // TODO: navigation vers messagerie privée
  };

  if (loading) {
    return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-neutral-400">Chargement du profil d&apos;artiste...</p>
        </div>
      </div>
    );
  }

  if (error || !artistData?.artist) {
    return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
      <div className="bg-white dark:bg-neutral-900/70 rounded-lg shadow-sm p-8 border border-gray-200 dark:border-neutral-800">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Music className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Profil non disponible</h2>
            <p className="text-gray-600 mb-6">
        {error || 'Aucun profil d\'artiste n\'est actuellement disponible.'}
            </p>
            <button
              onClick={() => window.location?.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { artist, agents, influencers, bookings } = artistData;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Music },
    { id: 'agents', label: 'Agents', icon: Users },
    { id: 'media', label: 'Médias', icon: Camera },
    { id: 'performances', label: 'Performances', icon: Calendar },
    { id: 'booking', label: 'Réservation', icon: CheckCheck },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 text-gray-900 dark:text-neutral-100">
      {/* Profile Header */}
      <ProfileHeader
        artist={artist}
        agents={agents}
        influencers={influencers}
        totalPerformances={bookings?.length || 0}
      />
      {/* Navigation Tabs */}
  <div className="bg-white dark:bg-neutral-900/70 border-b border-gray-200 dark:border-neutral-800 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {tabs?.map((tab) => {
              const IconComponent = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About Section */}
              <div className="bg-white dark:bg-neutral-900/70 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-neutral-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 mb-4">À propos</h3>
                <p className="text-gray-600 dark:text-neutral-400 leading-relaxed">
                  {artist?.description || artist?.professional_bio || `${artist?.business_name} est un artiste talentueux avec une expérience significative dans l'industrie musicale. Spécialisé dans des performances captivantes et des événements mémorables.`}
                </p>
                
                {/* Profession and Skills */}
                {artist?.partner_profession_data?.[0] && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-neutral-200 mb-3">Profession</h4>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                      {artist?.partner_profession_data?.[0]?.profession || artist?.artist_profession}
                    </span>
                    
                    {artist?.partner_profession_data?.[0]?.skill_tags?.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-neutral-200 mb-3">Compétences</h4>
                        <div className="flex flex-wrap gap-2">
                          {artist?.partner_profession_data?.[0]?.skill_tags?.map((skill, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Musical Specialties */}
                {artist?.partner_musical_specialties?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-neutral-200 mb-3">Spécialités musicales</h4>
                    <div className="flex flex-wrap gap-2">
                      {artist?.partner_musical_specialties?.map((specialty, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {specialty?.specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Media */}
                {artist?.partner_social_links?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Réseaux sociaux</h4>
                    <div className="flex flex-wrap gap-3">
                      {artist?.partner_social_links?.map((social, index) => (
                        <a
                          key={index}
                          href={social?.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <span className="text-sm font-medium capitalize">{social?.platform}</span>
                          {social?.is_verified && (
                            <CheckCheck className="w-4 h-4 text-blue-600" />
                          )}
                          {social?.follower_count > 0 && (
                            <span className="text-xs text-gray-500">
                              {social?.follower_count?.toLocaleString()}
                            </span>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">
                      {artist?.city ? `${artist?.city}, ${artist?.country}` : 'Localisation non renseignée'}
                    </span>
                  </div>
                  {artist?.phone && (
                    <div className="flex items-center text-sm">
                      <Phone className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-600">{artist?.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm">
                    <Mail className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                    <span className="text-gray-600">{artist?.email}</span>
                  </div>
                  {artist?.website_url && (
                    <div className="flex items-center text-sm">
                      <Globe className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                      <a
                        href={artist?.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Site web
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{artist?.total_bookings || 0}</div>
                    <div className="text-sm text-gray-500">Réservations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {artist?.total_earnings ? `€${Math.round(artist?.total_earnings)}` : '€0'}
                    </div>
                    <div className="text-sm text-gray-500">Revenus</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{agents?.length || 0}</div>
                    <div className="text-sm text-gray-500">Agents</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{influencers?.length || 0}</div>
                    <div className="text-sm text-gray-500">Influenceurs</div>
                  </div>
                </div>
              </div>

              {/* Portfolio Media Preview */}
              {artist?.partner_portfolio_media?.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {artist?.partner_portfolio_media?.slice(0, 6)?.map((media, index) => (
                      <div key={media?.id || index} className="relative group">
                        <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                          {media?.media_type === 'video' ? (
                            <video
                              src={media?.file_url}
                              poster={media?.thumbnail_url}
                              className="w-full h-32 object-cover"
                             
                              muted
                            />
                          ) : (
                            <div className="w-full h-32 bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                              <Music className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          {media?.is_featured && (
                            <div className="absolute top-2 left-2">
                              <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                                Vedette
                              </span>
                            </div>
                          )}
                        </div>
                        <h4 className="text-sm font-medium text-gray-900 mt-2 truncate">
                          {media?.title}
                        </h4>
                        <p className="text-xs text-gray-500 capitalize">{media?.category}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Agent Section */}
              <AgentSection agents={agents} onContactAgent={handleContactAgent} />
              
              {/* Influencer Metrics */}
              <InfluencerMetrics influencers={influencers} />
            </div>
          </div>
        )}

        {activeTab === 'agents' && (
          <AgentSection agents={agents} onContactAgent={handleContactAgent} detailed />
        )}

        {activeTab === 'media' && (
          <MediaGallery artistId={artist?.id} />
        )}

        {activeTab === 'performances' && (
          <PerformanceHistory bookings={bookings} />
        )}

        {activeTab === 'booking' && (
          <BookingAvailability artist={artist} />
        )}
      </div>
    </div>
  );
};

export default EnhancedArtistProfile;